import { startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { isFutureEffectiveDate } from '~/business-logic/billingLogic';
import { BillingStageScheduleType } from '~/models';
import { fromDateStampObject } from '~/models/time/translator';
import { formatLongMonthDateWithLocalTimezone } from '~/utils/date-formatting';
import { utils } from '..';
export const bulletPointsTextsPath = 'billing.bulletPoints';
export const allStagesHaveSchedule = (stages)=>stages.length > 0 && stages.every((stage)=>stage.stageSchedule !== undefined);
export function legacyInStagesBullet(params) {
    const { oneTimeInStages, stages, bulletParams } = params;
    const { texts, agreementData, now } = bulletParams;
    const amounts = stages.map((s)=>utils.formatDollarAmount(s.discountedFinalAmount)).join(', ');
    const wasApprovedWithFutureEffectiveDate = agreementData.daysToEffectiveDate > 0;
    const autoInvoiceIssued = isFutureEffectiveDate(agreementData.effectiveDate, now) || wasApprovedWithFutureEffectiveDate ? texts.onEffectiveDate() : texts.uponAgreementApproval();
    const terms = oneTimeInStages.isAutoFirst() ? texts.firstInvoiceAutoFirst({
        autoInvoiceIssued
    }) : texts.invoicesPerTerms();
    return texts.billedInStages({
        count: stages.length,
        amounts,
        terms
    });
}
function effectiveDateOrNull(agreementData) {
    return agreementData.effectiveDate ? fromDateStampObject(agreementData.effectiveDate) : null;
}
function isPastOrToday(resolved, now) {
    return startOfDay(resolved).getTime() <= startOfDay(now).getTime();
}
function categorizeStage(stage, agreementData, now) {
    const effectiveDate = effectiveDateOrNull(agreementData);
    const effDateIsFuture = effectiveDate != null && !isPastOrToday(effectiveDate, now);
    const amount = utils.formatDollarAmount(stage.discountedFinalAmount);
    switch(stage.stageSchedule.type){
        case BillingStageScheduleType.OnEffectiveDate:
            if (effDateIsFuture) return {
                category: 'specificDate',
                amount,
                date: effectiveDate
            };
            return {
                category: 'onAcceptance',
                amount
            };
        case BillingStageScheduleType.RelativeToEffectiveDate:
            {
                const { days } = stage.stageSchedule.relativeToEffectiveDate;
                if (effDateIsFuture) {
                    const resolved = new Date(effectiveDate);
                    resolved.setDate(resolved.getDate() + days);
                    return {
                        category: 'specificDate',
                        amount,
                        date: resolved
                    };
                }
                return {
                    category: 'relativeToAcceptance',
                    amount,
                    days
                };
            }
        case BillingStageScheduleType.OnDate:
            {
                const resolved = new Date(stage.stageSchedule.onDate.date);
                if (isPastOrToday(resolved, now)) return {
                    category: 'onAcceptance',
                    amount
                };
                return {
                    category: 'specificDate',
                    amount,
                    date: resolved
                };
            }
        case BillingStageScheduleType.Manual:
        case BillingStageScheduleType.RequiresDateSelection:
            return {
                category: 'manual',
                amount
            };
    }
}
function stageToBulletText(stage, texts) {
    switch(stage.category){
        case 'onAcceptance':
            return texts.stage.invoiceOnProposalAcceptance({
                amount: stage.amount
            });
        case 'relativeToAcceptance':
            return texts.stage.invoiceDaysAfterProposalAcceptance({
                amount: stage.amount,
                days: stage.days
            });
        case 'specificDate':
            return texts.stage.invoiceOnDate({
                amount: stage.amount,
                date: formatLongMonthDateWithLocalTimezone(stage.date)
            });
        case 'manual':
            return null;
    }
}
function aggregateManualBullet(manualStages, texts) {
    if (manualStages.length === 0) return [];
    if (manualStages.length === 1) {
        return [
            texts.stage.remainingInvoiceManual({
                amount: manualStages[0].amount
            })
        ];
    }
    const amounts = manualStages.map((s)=>s.amount).join(', ');
    return [
        texts.stage.remainingInvoicesManual({
            amounts
        })
    ];
}
export function automaticStagesBullets(params) {
    const { stagesWithSchedule, bulletParams } = params;
    const { texts, agreementData, now } = bulletParams;
    const categorized = stagesWithSchedule.map((s)=>categorizeStage(s, agreementData, now));
    const onAcceptance = categorized.filter((s)=>s.category === 'onAcceptance');
    const relative = categorized.filter((s)=>s.category === 'relativeToAcceptance');
    const specificDate = categorized.filter((s)=>s.category === 'specificDate');
    const manual = categorized.filter((s)=>s.category === 'manual');
    const orderedBullets = compact([
        ...onAcceptance.map((s)=>stageToBulletText(s, texts)),
        ...relative.map((s)=>stageToBulletText(s, texts)),
        ...specificDate.map((s)=>stageToBulletText(s, texts)),
        ...aggregateManualBullet(manual, texts)
    ]);
    return [
        texts.stage.minisite.header({
            count: stagesWithSchedule.length
        }),
        ...orderedBullets.map((text)=>({
                text,
                indented: true
            }))
    ];
}
